import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { LoginService, StorageService } from '@uoa/auth';
import { BypassErrorService, UoaErrorsConfig } from '@uoa/error-pages';
import { SUPPRESS_ERROR_TOAST } from '../interceptors/error-interceptor';
import {BehaviorSubject, Observable, ReplaySubject, Subject, firstValueFrom, takeUntil} from 'rxjs';
import { environment } from 'src/environments/environment';
import { CatchitEnvironment, CatchitRole } from '../interfaces/environment.interface';
import { Line } from '../dto/line';
import { Area } from '../dto/area';
import { Person } from '../dto/person';
import { getGroupsFromIdToken, getIdentityFromIdToken } from '../utils/cognito-token.util';
import {AreaService} from "./area.service";
import {LineService} from "./line.service";
import { CatchitStorageService } from './catchit-storage.service';
import { NetworkDetector } from './network-detector.service';
import { ExternalLoginService } from './external-login.service';

export { CatchitRole };

// How long a cached profile (used when the person/admin-area API calls fail, e.g. offline)
// stays valid for. Deliberately long - this exists to bridge real offline periods, not to
// act as a short-lived request cache (that's personLookupCache below).
const PROFILE_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Subset of CatchitUser that can't be re-derived from the ID token alone - everything here
// came from a Person/admin-area API lookup, so it's what needs caching for offline reuse.
interface CachedUserProfile {
  id: string;
  phoneNumber?: string;
  address?: string;
  areaId?: number;
  lineId?: number;
  paid: boolean;
  showWelcome: boolean;
  role: CatchitRole | null;
  adminAreas: Area[];
}

export interface CatchitUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  phoneNumber?: string;
  address?: string;
  /** Real UoA EmpID (custom:EmpID) and UPI, only present for SAML-federated (UoA SSO) logins. */
  empId?: string;
  upi?: string;
  /** Native Cognito group membership (e.g. IdP/federation groups from the `cognito:groups` claim). */
  cognitoGroups: string[];
  /** Catch IT application role groups (e.g. "CatchitOwner_users") from the `custom:Groups` claim. */
  customGroups: string[];
  /** Resolved once from cognitoGroups/customGroups against environment.allowedGroups - the single source of truth for access control. Null if the user isn't a member of any configured group, meaning no access. */
  role: CatchitRole | null;
  /**
   * Whether a matching Person record actually exists in CatchIT's own database - distinct
   * from `role`, which can be granted entirely by an external IdP/Grouper entitlement with
   * no corresponding Person record at all (confirmed live: an account can resolve a valid
   * `role` from SAML/Cognito group claims while having never been registered in CatchIT).
   * 'unknown' means the lookup itself failed (offline/server error) - callers must not treat
   * that the same as 'not-found', or a connectivity blip would look identical to "not
   * registered" and incorrectly lock a real user out.
   */
  personRecordStatus?: 'found' | 'not-found' | 'unknown';
  areaId?: number;
  lineId?: number;
  paid: boolean;
  showWelcome: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CognitoUserService implements OnDestroy {
  private currentUser$ = new BehaviorSubject<CatchitUser | null>(null);
  private env: CatchitEnvironment;

  // Additional properties for compatibility with the original UserService
  public defaultAreaAndLineChanged = new ReplaySubject<boolean>();
  private destroyed: Subject<boolean> = new Subject<boolean>();
  private currentLine: Line | null = null;
  private currentArea: Area | null = null;
  private defaultArea: Area | null = null;
  private defaultLine: Line | null = null;
  private showMap = true;
  private adminAreas: Area[] = [];
  private loadingAdminAreas = false;

  // Caches the DB Person lookup result for the session, so repeated loadCurrentUser() calls
  // (e.g. from isAdmin()/isSuperAdmin()) don't re-fetch the full person list every time.
  // undefined = not yet attempted, null = attempted and no match found.
  private personLookupCache: Person | null | undefined = undefined;
  // True once the one Person lookup attempt this session has failed (network/API error, as
  // opposed to a legitimate "no matching Person record"). Since personLookupCache is never
  // retried once set, this needs to persist across calls the same way personLookupCache does.
  private personLookupFailed = false;
  // Same session-cache pattern for the admin-areas lookup - see enrichWithAdminAreas().
  private adminAreasLookupAttempted = false;
  private adminAreasLookupFailed = false;

  constructor(
    private loginService: LoginService,
    private areaService: AreaService,
    private lineService: LineService,
    private storageService: StorageService,
    private catchitStorageService: CatchitStorageService,
    private http: HttpClient,
    private bypassErrorService: BypassErrorService,
    private errorPagesConfig: UoaErrorsConfig,
    private networkDetector: NetworkDetector,
    private externalLoginService: ExternalLoginService
  ) {
    this.env = environment as CatchitEnvironment;
      if (this.adminAreas == null) {
          this.setupAvailableAreas();
      }
  }

  /**
   * Get current user observable
   */
  getCurrentUser(): Observable<CatchitUser | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Load user from UoA auth service and map to CatchitUser
   */
  async loadCurrentUser(): Promise<CatchitUser | null> {
    try {
      // For local development: use mock user with admin permissions
      if (!this.env.production && window.location.hostname === 'mock') {
          const mockUser = this.getMockUser();
          this.currentUser$.next(mockUser);
        return mockUser;
      }

      // Production/remote: use real authentication
      // Add timeout for Firefox compatibility - isAuthenticated() can hang
      let isAuthenticated = await Promise.race([
        this.loginService.isAuthenticated(),
        new Promise<boolean>((_, reject) =>
          setTimeout(() => reject(new Error('Authentication check timeout')), 5000)
        )
      ]);
      // isAuthenticated() only means "access token not yet expired". Online, the route's
      // AuthGuard has already refreshed it by now; offline it can't, and treating that as
      // logged out sent field users to /error/401 (confirmed live on dev: expired token,
      // offline, open /offline-enter-data). Same rule as RequireAuthGuard - the session
      // stands unless Cognito actually rejects the refresh token; the ID token's identity
      // claims don't change just because its 60-minute validity lapsed.
      if (!isAuthenticated) {
        isAuthenticated = await this.externalLoginService.refreshSession() !== 'rejected';
      }
      if (!isAuthenticated) {
        this.currentUser$.next(null);
        return null;
      }

      // @uoa/auth's own LoginService.getUserInfo()/AuthService.getUserInfos()
      // unconditionally reads decodedToken['identities'][0].userId to get the UPI,
      // which only exists on SAML-federated (UoA SSO) tokens - it throws for native
      // Cognito users (external logins). Its custom:Groups parsing is also broken
      // (see getGroupsFromIdToken). So we decode the ID token ourselves entirely,
      // rather than calling into the library for any of this.
      const idToken = await this.storageService.getItem('idToken');
      if (!idToken) {
        this.currentUser$.next(null);
        return null;
      }

      const catchitUser = this.buildCatchitUserFromIdToken(idToken);
      const personFetchFailed = await this.enrichWithPersonRecord(catchitUser);
      const adminAreasFetchFailed = await this.enrichWithAdminAreas(catchitUser);

      // If either DB-backed lookup failed (e.g. offline), fall back to whatever was cached
      // from the last successful load rather than leaving the user with blank/default fields
      // and a demoted role. If both succeeded, refresh the cache with the latest values.
      if (personFetchFailed || adminAreasFetchFailed) {
        await this.applyCachedProfileFallback(catchitUser);
      } else {
        await this.persistProfileCache(catchitUser);
      }

      // setupDefaultLineAndArea() (below) is the only thing that ever populates
      // defaultArea/defaultLine, but nothing called it - setCurrentUser()/setDefaultArea()/
      // setDefaultLine() (the only callers/other setters) are dead code, never invoked
      // anywhere in the app. getDefaultArea()/getDefaultLine() had therefore always
      // returned null, and getCurrentArea()'s fallback to them was silently broken app-wide -
      // confirmed live on Profile: saving a new default area/line reported success and the
      // DB genuinely updated, but AreaLineComponent's showDefaultAreaAndLine=true branch
      // always got (null, null) back, which processAreaData() then covers by auto-selecting
      // the alphabetically-first area (coincidentally "Alberon Reserve") - masking the fact
      // that the true saved default was never actually being read at all. Using
      // personLookupCache here (rather than catchitUser) since it's the real Person entity
      // setupDefaultLineAndArea() needs, not the enriched CatchitUser shape.
      if (this.personLookupCache) {
        await this.setupDefaultLineAndArea(this.personLookupCache);
      }

      this.currentUser$.next(catchitUser);
      return catchitUser;
    } catch (error) {
      console.error('Error loading current user:', error);
      this.currentUser$.next(null);
      return null;
    }
  }

    private getMockUser() {
        console.log('CatchitUserService: Using mock user for local development');
        const mockCognitoGroups = ['Catchit_users', 'external'];
        const mockCustomGroups = ['CatchitOwner_users', 'CatchitAdmin_users'];
        const mockUser: CatchitUser = {
            id: '1',
            email: 'mock@test.com',
            firstName: 'Test',
            lastName: 'User',
            username: 'mock-user',
            phoneNumber: '+64 21 123 4567',
            cognitoGroups: mockCognitoGroups,
            customGroups: mockCustomGroups,
            role: this.deriveRole(mockCognitoGroups),
            areaId: 8,
            lineId: 1,
            paid: true,
            showWelcome: true
        };
        return mockUser;
    }

    /**
   * Update user profile attributes in Cognito
   * Note: Group membership is managed separately by administrators
   */
  async updateUserProfile(attributes: Partial<CatchitUser>): Promise<void> {
    const cognitoAttributes: any = {};

    if (attributes.firstName) cognitoAttributes.given_name = attributes.firstName;
    if (attributes.lastName) cognitoAttributes.family_name = attributes.lastName;
    if (attributes.phoneNumber) cognitoAttributes.phone_number = attributes.phoneNumber;

    // Map custom attributes (groups are handled separately)
    if (attributes.areaId !== undefined && this.env.cognitoCustomAttributes) {
      cognitoAttributes[this.env.cognitoCustomAttributes.areaId] = attributes.areaId.toString();
    }
    if (attributes.lineId !== undefined && this.env.cognitoCustomAttributes) {
      cognitoAttributes[this.env.cognitoCustomAttributes.lineId] = attributes.lineId.toString();
    }
    if (attributes.paid !== undefined && this.env.cognitoCustomAttributes) {
      cognitoAttributes[this.env.cognitoCustomAttributes.paid] = attributes.paid.toString();
    }
    if (attributes.showWelcome !== undefined && this.env.cognitoCustomAttributes) {
      cognitoAttributes[this.env.cognitoCustomAttributes.showWelcome] = attributes.showWelcome.toString();
    }

    // Note: Profile updates would need to be handled by backend API
    // The UoA auth service doesn't provide direct attribute update capability
    console.warn('Profile updates need to be implemented via backend API');

    // Reload user to reflect changes
    await this.loadCurrentUser();
  }

  /**
   * Resolve a user's role from their group membership against environment.allowedGroups,
   * in priority order (first configured entry that matches wins). Returns null when the
   * user isn't a member of any configured group - such users have no access.
   *
   * Deliberately checks ONLY cognitoGroups (real Cognito User Pool Group membership,
   * which CatchIT itself controls via AdminAddUserToGroup during registration) - never
   * customGroups (the custom:Groups SAML attribute, asserted by UoA's own IdP and
   * entirely outside CatchIT's control). A UoA SSO login whose SAML assertion happens to
   * carry a custom:Groups value matching one of these group names would otherwise grant
   * full CatchIT access with zero registration and no admin ever having approved them -
   * confirmed live: a UoA test identity with custom:Groups='CatchitOwner_users' baked
   * into its SAML assertion got super-admin access despite having no Person record at
   * all. Native/external and properly-registered UoA users are unaffected, since
   * registration already grants access via AdminAddUserToGroup (real Cognito groups),
   * not custom:Groups.
   */
  private deriveRole(cognitoGroups: string[]): CatchitRole | null {
    const match = this.env.allowedGroups?.find(entry => cognitoGroups.includes(entry.group));
    return match?.role ?? null;
  }

  private isAdminRole(role: CatchitRole | null | undefined): boolean {
    return role === 'admin' || role === 'super-admin';
  }

  /**
   * Synchronous permission check using the already-resolved role on the current user
   */
  isAdminSync(): boolean {
    return this.isAdminRole(this.currentUser$.value?.role);
  }

  /**
   * Synchronous permission check using the already-resolved role on the current user
   */
  isSuperAdminSync(): boolean {
    return this.currentUser$.value?.role === 'super-admin';
  }

  /**
   * Synchronous permission check using the already-resolved role on the current user
   */
  isAdminOrSuperAdminForAnyAreaSync(): boolean {
    return this.isAdminSync();
  }

  /**
   * Check if current user is admin or super admin (async version for backward compatibility)
   */
  async isAdmin(): Promise<boolean> {
    const user = await this.loadCurrentUser();
    return this.isAdminRole(user?.role);
  }

  /**
   * Check if current user is super admin
   */
  async isSuperAdmin(): Promise<boolean> {
    const user = await this.loadCurrentUser();
    return user?.role === 'super-admin';
  }

  /**
   * Check if user is admin or super admin for any area
   */
  async isAdminOrSuperAdminForAnyArea(): Promise<boolean> {
    return this.isAdmin();
  }

  /**
   * Check if user is in specific group. Checked against cognitoGroups only - see
   * deriveRole() for why customGroups (UoA's own SAML-asserted claim) is never trusted
   * for access decisions.
   */
  async isInGroup(groupName: string): Promise<boolean> {
    const user = await this.loadCurrentUser();
    if (!user) return false;
    return user.cognitoGroups.includes(groupName);
  }

  /**
   * Get user's role, resolved once at load time from group membership.
   * Null if the user isn't a member of any configured group (no access).
   */
  getUserRole(user?: CatchitUser): CatchitRole | null {
    const currentUser = user || this.currentUser$.value;
    return currentUser?.role ?? null;
  }

  /**
   * Get user display name
   */
  getDisplayName(user?: CatchitUser): string {
    const currentUser = user || this.currentUser$.value;
    if (!currentUser) return 'Unknown User';
    return `${currentUser.firstName} ${currentUser.lastName} [${currentUser.username}]`;
  }

  /**
   * Clear current user (logout)
   */
  clearCurrentUser(): void {
    if (this.currentUser$.value != null && this.currentUser$.value.id != null) {
        // sessionStorage.removeItem('userLoggedIn');
        localStorage.removeItem('person_' + this.currentUser$.value.id);
    }
    this.currentUser$.next(null);
    this.personLookupCache = undefined;
    this.personLookupFailed = false;
    this.adminAreasLookupAttempted = false;
    this.adminAreasLookupFailed = false;
  }

  /**
   * Destroy method for cleanup
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.unsubscribe();
  }

  /**
   * Build a CatchitUser directly from the raw ID token. Deliberately bypasses
   * @uoa/auth's own UserInfoDto mapping entirely - see the comment in
   * loadCurrentUser() for why.
   */
  private buildCatchitUserFromIdToken(idToken: string): CatchitUser {
    const identity = getIdentityFromIdToken(idToken);
    const { cognitoGroups, customGroups } = getGroupsFromIdToken(idToken);

    return {
      // empId (custom:EmpID) is only reliably present for SAML-federated (UoA) users.
      // Native/external users currently have no numeric identifier here - see .claude/auth.md.
      id: identity.empId || identity.upi || '',
      email: identity.email,
      firstName: identity.firstName,
      lastName: identity.lastName,
      username: identity.upi || identity.cognitoUsername || identity.email,
      empId: identity.empId,
      upi: identity.upi,
      cognitoGroups,
      customGroups,
      role: this.deriveRole(cognitoGroups),
      // Custom attributes are fetched from the person lookup
      phoneNumber: undefined,
      areaId: undefined,
      lineId: undefined,
      paid: false,
      showWelcome: true
    };
  }

  /** Returns true if the Person lookup failed (network/API error) rather than legitimately finding no match. */
  private async enrichWithPersonRecord(catchitUser: CatchitUser): Promise<boolean> {
    if (this.personLookupCache === undefined) {
      try {
          const baseUrl = this.env.apiBaseUrl.replace(/\/$/, '');
          const url = `${baseUrl}/persons/${catchitUser.id}`;
          // A brand-new UoA SSO login with no matching Person record yet is an expected,
          // legitimate case (not yet registered), not a real error - bypass
          // @uoa/error-pages' automatic redirect-to-/error/404 for this one request so
          // it's handled gracefully below instead of yanking the user to a generic
          // "Page Not Found" page.
          // Offline, bypass every error code instead - same rule as CatchitHttpService, which
          // this direct HttpClient call doesn't go through. Opening the app offline otherwise
          // hard-navigated to /error/504 from this startup lookup (confirmed live on dev).
          this.bypassErrorService.bypassError(url, this.networkDetector.getStatus().connected
              ? [404]
              : [...this.errorPagesConfig.clientErrorCodes, ...this.errorPagesConfig.serverErrorCodes]);
          this.personLookupCache = await firstValueFrom(this.http.get<Person>(url, {
            context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true)
          })) ?? null;
      } catch (error) {
        // A clean 404 means we successfully asked and the answer is "no Person record" -
        // that's the legitimate "not registered" case the comment above already describes,
        // not a failure. Only genuine network/server errors (any other status, or none at
        // all) should count as "couldn't determine" - conflating the two here previously
        // meant a real 404 and a mere connectivity blip were indistinguishable to callers.
        if ((error as { status?: number })?.status === 404) {
          this.personLookupCache = null;
        } else {
          console.error('CatchitUserService: failed to look up matching Person record:', error);
          this.personLookupCache = null;
          this.personLookupFailed = true;
        }
      }
    }

    if (this.personLookupCache) {
      const person = this.personLookupCache;
      catchitUser.id = person.id.toString();
      catchitUser.phoneNumber = person.phoneNumber || catchitUser.phoneNumber;
      catchitUser.areaId = person.areaId;
      catchitUser.lineId = person.lineId;
      catchitUser.address = person.address;
      // Backend Person.paid is a Java String column, not a boolean - arrives over JSON as
      // "true"/"false" despite the frontend Person DTO declaring it as boolean.
      catchitUser.paid = (person.paid as unknown) === true || (person.paid as unknown) === 'true';
      catchitUser.showWelcome = person.showWelcome;
      catchitUser.personRecordStatus = 'found';
    } else if (this.personLookupFailed) {
      catchitUser.personRecordStatus = 'unknown';
    } else {
      console.warn('CatchitUserService: no matching Person record found in the database for', catchitUser.email);
      catchitUser.personRecordStatus = 'not-found';
    }

    return this.personLookupFailed;
  }

  /**
   * Admin status isn't Cognito-group-based - it's DB-driven, per area (see the
   * CatchitOwner_users/Catchit_users groups only covering super-admin/user in
   * environment.allowedGroups). Populates adminAreas from the Person lookup's ID
   * and promotes role to 'admin' if the user administers at least one area and
   * isn't already super-admin.
   */
  /** Returns true if the admin-areas lookup failed (network/API error) rather than legitimately finding none. */
  private async enrichWithAdminAreas(catchitUser: CatchitUser): Promise<boolean> {
    const personId = this.parseNumber(catchitUser.id);
    if (personId == null) {
      return false;
    }

    // Cached for the session, same pattern as personLookupCache/personLookupFailed below -
    // this used to re-fetch on every single loadCurrentUser() call (every reload, every
    // debug button click, every route guard check), which is enough real traffic in a normal
    // session to trip the gateway's rate limiting.
    if (!this.adminAreasLookupAttempted) {
      try {
        this.adminAreas = await firstValueFrom(this.areaService.getAllAdminAreasByPersonId(personId)) ?? [];
        this.adminAreasLookupAttempted = true;
      } catch (error) {
        console.error('CognitoUserService: failed to look up admin areas:', error);
        this.adminAreas = [];
        this.adminAreasLookupFailed = true;
      }
    }

    if (this.adminAreas.length > 0 && catchitUser.role !== 'super-admin') {
      catchitUser.role = 'admin';
    }
    return this.adminAreasLookupFailed;
  }

  /**
   * Persists the DB-derived parts of catchitUser (Person fields, role, adminAreas) to
   * CatchitStorageService so a future loadCurrentUser() can fall back to them if the
   * backend is unreachable (e.g. offline PWA usage). See .claude/auth.md.
   */
  private async persistProfileCache(catchitUser: CatchitUser): Promise<void> {
    try {
      const cached: CachedUserProfile = {
        id: catchitUser.id,
        phoneNumber: catchitUser.phoneNumber,
        address: catchitUser.address,
        areaId: catchitUser.areaId,
        lineId: catchitUser.lineId,
        paid: catchitUser.paid,
        showWelcome: catchitUser.showWelcome,
        role: catchitUser.role,
        adminAreas: this.adminAreas,
      };
      await this.catchitStorageService.cacheUserProfile(catchitUser.username, cached);
    } catch (error) {
      console.error('CatchitUserService: failed to cache user profile for offline use:', error);
    }
  }

  /**
   * Fills in catchitUser's DB-derived fields from the last cached profile, used when the
   * live Person/admin-area lookups fail. Never downgrades a role already resolved from the
   * ID token itself (super-admin/user survive offline regardless) - only ever promotes to
   * 'admin', matching enrichWithAdminAreas()'s own rule.
   */
  private async applyCachedProfileFallback(catchitUser: CatchitUser): Promise<void> {
    try {
      const cached: CachedUserProfile | null =
        await this.catchitStorageService.getCachedUserProfile(catchitUser.username, PROFILE_CACHE_MAX_AGE_MS);
      if (!cached) {
        return;
      }

      catchitUser.id = cached.id || catchitUser.id;
      catchitUser.phoneNumber = cached.phoneNumber;
      catchitUser.address = cached.address;
      catchitUser.areaId = cached.areaId;
      catchitUser.lineId = cached.lineId;
      catchitUser.paid = cached.paid;
      catchitUser.showWelcome = cached.showWelcome;

      if (cached.role === 'admin' && catchitUser.role !== 'super-admin') {
        catchitUser.role = 'admin';
      }
      if (this.adminAreas.length === 0 && cached.adminAreas?.length) {
        this.adminAreas = cached.adminAreas;
      }
    } catch (error) {
      console.error('CatchitUserService: failed to read cached user profile:', error);
    }
  }

  private parseNumber(value?: string): number | undefined {
    if (!value) return undefined;
    const num = parseInt(value, 10);
    return isNaN(num) ? undefined : num;
  }

  private parseBoolean(value?: string): boolean {
    return value === 'true';
  }

  // Compatibility methods for original UserService interface

  getCurrentPersonIdSync(): number | undefined {
    return this.parseNumber(this.currentUser$.value?.id);
  }

  /**
   * Get current person synchronously (compatibility method)
   * Returns Person interface for compatibility with original components
   */
  getCurrentPersonSync(): Person | null {
    const catchitUser = this.currentUser$.value;
    if (!catchitUser) return null;

    // Convert CatchitUser to Person for compatibility. A real Person instance (not a
    // plain object literal cast to Person) so getDisplayName()/getDisplayNameForPerson()
    // live on the prototype, not as own function-valued properties - confirmed live that
    // storing this object (e.g. as an offline line check's checkBy) in IndexedDB threw
    // DataCloneError otherwise, since functions can't be structurally cloned.
    return Object.assign(new Person(), {
      id: parseInt(catchitUser.id, 10) || 0,
      firstName: catchitUser.firstName,
      lastName: catchitUser.lastName,
      username: catchitUser.username,
      email: catchitUser.email,
      phoneNumber: catchitUser.phoneNumber || '',
      address: catchitUser.address ||'',
      paid: catchitUser.paid,
      superAdmin: catchitUser.role === 'super-admin',
      showWelcome: catchitUser.showWelcome,
      areaId: catchitUser.areaId || 0,
      lineId: catchitUser.lineId || 0,
    });
  }

  isMapShown(): boolean {
    return this.showMap;
  }

  setMapShown(shown: boolean): void {
    this.showMap = shown;
  }

  getCurrentArea(adminAreaRequired = false): Area | null {
      if (this.currentArea != null) {
          return this.currentArea;
      }
      if (adminAreaRequired) {
          if (this.currentArea && this.isAnAreaTheUserHasAdminPermissionsFor(this.currentArea.id)) {
              return this.currentArea;
          } else if (this.isAnAreaTheUserHasAdminPermissionsFor(this.getDefaultArea()?.id)) {
              return this.getDefaultArea();
          } else if (this.adminAreas?.length > 0) {
              return this.adminAreas[0];
          } else {
              // TODO This might cause an error??
              return null;
          }
      }
      if (this.currentArea) {
          return this.currentArea;
      } else {
          return this.getDefaultArea();
      }
  }

  setCurrentArea(area: Area): void {
    // Guards against a feedback loop: AreaLineComponent listens to
    // defaultAreaAndLineChanged and reacts by re-fetching and re-selecting an
    // area/line, which (via getLinesForArea()) calls back into
    // setCurrentArea()/setCurrentLine() - broadcasting the same event again even
    // when nothing actually changed, looping forever. Only broadcast on a real change.
    if (this.currentArea?.id === area?.id) {
      return;
    }
    this.currentArea = area;
    this.defaultAreaAndLineChanged.next(true);
  }

  getCurrentLine(): Line | null {
    return this.currentLine;
  }

  setCurrentLine(line: Line): void {
    if (this.currentLine?.id === line?.id) {
      return;
    }
    this.currentLine = line;
    this.defaultAreaAndLineChanged.next(true);
  }

  getDefaultArea(): Area | null {
    return this.defaultArea;
  }

  getDefaultLine(): Line | null {
    return this.defaultLine;
  }

  /**
   * Set current user (compatibility method)
   */
  setCurrentUser(person: Person): void {
    // Convert Person to CatchitUser
    const catchitUser: CatchitUser = {
      id: person.id.toString(),
      email: person.email,
      firstName: person.firstName,
      lastName: person.lastName,
      username: person.username,
      phoneNumber: person.phoneNumber,
      cognitoGroups: [],
      customGroups: [],
      role: person.superAdmin ? 'super-admin' : 'user',
      areaId: person.areaId,
      lineId: person.lineId,
      paid: person.paid,
      showWelcome: person.showWelcome
    };
    this.currentUser$.next(catchitUser);

    void this.setupDefaultLineAndArea(person);
    this.setupAvailableAreas();
  }

  public isAdminOfArea(area: Area): boolean {
      if (this.adminAreas == null && !this.loadingAdminAreas) {
          this.setupAvailableAreas();
      }
      if (area != null && this.adminAreas != null) {
          return this.adminAreas.findIndex(a => a.id === area.id) !== -1;
      }
      return false;
  }

  isAdminOrSuperAdmin(area: Area): boolean {
    return this.isAdminOfArea(area);
  }

  isAnAreaTheUserHasAdminPermissionsFor(areaId: number): boolean {
      return this.adminAreas?.filter(a => a.id === areaId).length > 0 || this.isSuperAdminSync();
  }

    private setupAvailableAreas(): void {
        if (this.currentUser$.value != null && this.currentUser$.value.id != null) {
            this.loadingAdminAreas = true;
            this.areaService.getAllAdminAreasByPersonId(+this.currentUser$.value.id)
                .pipe(takeUntil(this.destroyed)).subscribe((data: Area[]) => {
                this.adminAreas = data;
                this.loadingAdminAreas = false;
            }, () => {
                this.loadingAdminAreas = false;
            });
        }
    }

    private async setupDefaultLineAndArea(person: Person): Promise<void> {
        if (person.lineId == null && person.areaId == null) {
            // Something wrong - do nothing
        } else if (person.lineId == null && person.areaId != null) {
            this.defaultLine = null;
            try {
                this.defaultArea = await firstValueFrom(
                    this.areaService.getById(person.areaId).pipe(takeUntil(this.destroyed)));
            } catch {
                // Ignore error when no internet access
            }
        } else {
            try {
                this.defaultLine = await firstValueFrom(
                    this.lineService.getById(person.lineId).pipe(takeUntil(this.destroyed)));
            } catch {
                // Ignore error when no internet access
            }
            try {
                this.defaultArea = await firstValueFrom(
                    this.areaService.getByLineId(person.lineId).pipe(takeUntil(this.destroyed)));
            } catch {
                // Ignore error when no internet access
            }
            this.defaultsSetup();
        }
    }

    private defaultsSetup() {
        if (this.defaultArea != null || this.defaultLine != null) {
            this.defaultAreaAndLineChanged.next(true);
        }
    }

    setDefaultArea(selectedArea: Area) {
        if (selectedArea != null) {
            this.defaultArea = selectedArea;
            this.defaultAreaAndLineChanged.next(true);
        }
    }

    setDefaultLine(selectedLine: Line) {
        if (selectedLine != null) {
            this.defaultLine = selectedLine;
            this.defaultAreaAndLineChanged.next(true);
        }
    }
}